import type React from 'react';
import { useRef } from 'react';
import { useAppSelector } from '../../hooks';
import {
  selectAgentChatIcon,
  selectSize,
  selectThemeColor,
} from 'reducers/config';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import safeLinkifyHtml from 'utils/safeLinkifyHtml';
import headerLogoIcon from 'assets/icons/header-logo.svg';
import { selectLiveChatAgentIcon } from 'reducers/live-chat';
import Box from '@mui/material/Box';
import { Timestamp } from 'components/MessageStep/Timestamp';
import { selectIsMobile } from 'reducers/main';
import type { Size } from 'api/types';
import { selectMessageBubblePositions } from 'reducers/workflows';
import type { MessageBubblePosition } from 'utils/getMessageBubblePositions';
import type getMessageBubblePositions from 'utils/getMessageBubblePositions';
import {
  LIVE_CHAT_BACKGROUND_IMAGE_CSS,
  MESSAGE_PADDING,
} from 'embed/constants';
import sanitizeHtml from 'utils/sanitizeHtml';

interface MessageProps {
  agentIcon?: string;
  agentName?: string;
  /** String or Element to display as Agatha message */
  children: string | React.ReactElement;
  className?: string;
  hasLink?: boolean;
  hideBackground?: boolean;
  hideTimestamp?: boolean;
  isAdminOnly?: boolean;
  isAuthorIconVisible: boolean;
  isLiveChat?: boolean;
  /** used for components like metadata, article, attachments */
  nonMessageChildren?: React.ReactElement;
  stepIndex: number;
  timestamp?: number;
}

const AgathaMessage: React.FC<React.PropsWithChildren<MessageProps>> = ({
  agentIcon,
  agentName,
  children,
  className,
  hasLink = false,
  hideBackground = false,
  hideTimestamp = false,
  isAdminOnly = false,
  isAuthorIconVisible,
  isLiveChat = false,
  nonMessageChildren,
  stepIndex,
  timestamp,
}) => {
  const theme = useTheme();
  const themeColor = useAppSelector(selectThemeColor);
  const isChildrenString = typeof children === 'string' || children === null;
  const ref = useRef<HTMLDivElement>(null);
  const agentChatIcon = useAppSelector(selectAgentChatIcon);
  const liveChatAgentChatIcon = useAppSelector(selectLiveChatAgentIcon);
  const isMobile = useAppSelector(selectIsMobile);
  const size = useAppSelector(selectSize);
  const messageBubblePositions = useAppSelector(selectMessageBubblePositions);
  const messageBubblePosition = messageBubblePositions[stepIndex] ?? null;
  const shouldRenderMessageContainer = isChildrenString ? !!children : true;

  const getAgentChatIcon = () => {
    if (isAdminOnly) {
      return undefined;
    }

    if (isLiveChat) {
      return agentIcon || liveChatAgentChatIcon || headerLogoIcon;
    }

    return agentChatIcon;
  };

  // If there is no message to render and no non-message children, return null
  if (!shouldRenderMessageContainer && !nonMessageChildren) {
    return null;
  }

  return (
    <>
      {!hideTimestamp && (
        <Timestamp
          // 32px is the width of the AuthorIcon, 8px for overlap
          containerSx={{ marginBottom: '4px', marginLeft: 'calc(32px - 8px)' }}
          name={agentName}
          timestamp={timestamp}
        />
      )}
      <Box
        sx={{
          alignItems: 'flex-end',
          display: 'flex',
          flexDirection: 'row',
          marginBottom: '16px',
          marginLeft: `${isAuthorIconVisible ? '0' : '24px'}`,
          marginRight: {
            large: '120px',
            standard: '24px',
          },
          marginTop: 0,
        }}
      >
        {isAuthorIconVisible && (
          <AuthorIcon icon={getAgentChatIcon()} isLiveChat={isLiveChat} />
        )}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            gap: '4px',
            maxWidth: '100%',
          }}
        >
          {shouldRenderMessageContainer && (
            <AgathaMessageContainer
              backgroundColor={
                isAdminOnly
                  ? theme.palette.colors.yellow[200]
                  : theme.palette.colors.slate[100]
              }
              className={className}
              forethoughtLiveChat={isLiveChat}
              hasLink={hasLink}
              hideBackground={hideBackground}
              isMobile={isMobile}
              messageBubblePosition={messageBubblePosition}
              ref={ref}
              size={size}
            >
              {isChildrenString ? (
                <AgathaContent
                  backgroundColor={theme.palette.colors.slate[100]}
                  className='js-bot-message'
                  // eslint-disable-next-line react/forbid-component-props
                  dangerouslySetInnerHTML={{
                    __html: safeLinkifyHtml(
                      `
                      <p class="visually-hidden">Bot Response:</p>
                      <div dir="auto">
                        ${sanitizeHtml(children)}
                     </div>
                    `,
                    ),
                  }}
                  tabIndex={0}
                  themeColor={themeColor}
                />
              ) : (
                <AgathaContent
                  backgroundColor={theme.palette.colors.slate[100]}
                  className='js-bot-message'
                  dir='auto'
                  tabIndex={0}
                  themeColor={themeColor}
                >
                  <>
                    <p className='visually-hidden'>Bot Response:</p>
                    <div dir='auto'>{children}</div>
                  </>
                </AgathaContent>
              )}
            </AgathaMessageContainer>
          )}
          {nonMessageChildren}
        </Box>
      </Box>
    </>
  );
};

export function AuthorIcon({
  icon,
  isLiveChat,
}: {
  icon: string | undefined;
  isLiveChat: boolean;
}) {
  const theme = useTheme();

  if (!icon) {
    return null;
  }

  return (
    <Box
      sx={{
        ...(isLiveChat
          ? {
              '::after': {
                backgroundColor: theme.palette.colors.green[500],
                borderRadius: '100%',
                content: '""',
                display: 'block',
                height: '8px',
                outline: `2px solid ${theme.palette.colors.white}`,
                position: 'absolute',
                right: '0px',
                top: '24px',
                width: '8px',
              },
            }
          : {}),
        display: 'block',
        height: '32px',
        marginLeft: '-8px',
        position: 'relative',
        right: '-8px',
        width: '32px',
        zIndex: 1,
      }}
    >
      <Box
        alt=''
        component='img'
        src={icon}
        sx={{
          borderRadius: '50%',
          height: '32px',
          width: '32px',
        }}
      />
    </Box>
  );
}

const messageBubblePositionToRadiusMap: Record<MessageBubblePosition, string> =
  {
    'first-in-a-batch': '25px 25px 25px 8px',
    'last-in-a-batch': '8px 25px 25px 25px',
    lone: '25px',
    middle: '8px 25px 25px 8px',
  } as const;

export const AgathaMessageContainer = styled('div')<{
  backgroundColor: string;
  forethoughtLiveChat: boolean;
  hasLink?: boolean;
  hideBackground?: boolean;
  isMobile: boolean;
  marginBottom?: string;
  messageBubblePosition: ReturnType<typeof getMessageBubblePositions>[string];
  size: Size;
}>`
  white-space: normal;
  position: relative;
  padding: ${MESSAGE_PADDING};
  background-color: ${props => props.backgroundColor};
  border-radius: ${props =>
    props.messageBubblePosition?.bot
      ? messageBubblePositionToRadiusMap[props.messageBubblePosition?.bot]
      : '25px'};
  width: fit-content;
  align-self: none;
  min-height: 22px;
  word-break: break-word;
  max-width: ${props =>
    props.size === 'large' && !props.isMobile
      ? '100%'
      : 'calc(100% - 16px - 4px)'};

  ${props =>
    props.forethoughtLiveChat &&
    !props.hideBackground &&
    LIVE_CHAT_BACKGROUND_IMAGE_CSS}

  ${props =>
    (props.messageBubblePosition?.bot === 'first-in-a-batch' ||
      props.messageBubblePosition?.bot === 'middle') &&
    !props.hasLink &&
    'margin-bottom: -12px;'}

  ${props => props.marginBottom && `margin-bottom: ${props.marginBottom}`}
`;

// This has to be an element that permits flow content, because we allow HTML
// markup in messages, including `<p>` tags. So `styled('div')` would work here,
// `styled('p')` would not.
const AgathaContent = styled('div')<{
  backgroundColor: string;
  themeColor: string;
}>`
  border: 1px solid transparent;
  line-height: 22px;
  &:focus {
    outline: none;
  }
  &:focus-visible {
    border: 1px solid ${props => props.themeColor};
  }

  // Prevent excessive margin.
  // The first child is a visually hidden paragraph needed for accessibility,
  // so we need to target the second child, which is actual content:
  > :nth-child(2) > :first-child {
    margin-top: 0;
  }

  // Prevent excessive margin:
  > :last-child > :last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    p {
      margin: 0;
    }
  }

  a {
    overflow-wrap: break-word;
    color: var(--message-link-color);
  }

  a:visited {
    color: ${props => props.theme.palette.colors.purple[600]};
  }

  a:hover {
    color: ${props => props.theme.palette.colors.purple[600]};
    text-decoration: none;
  }

  a:active {
    color: ${props => props.theme.palette.colors.purple[700]};
  }

  & img {
    width: initial !important;
    height: initial !important;
    max-width: 100% !important;
  }

  & > p:empty {
    height: 20px;
  }
`;

export default AgathaMessage;
